import React, { useState, useEffect } from 'react';
import { Play, Square, RotateCw, FileText, Search, AlertTriangle, RefreshCw, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import processTrackerService from './api';
import DdlcPipelineLogs from './DdlcPipelineLogs';

const ProcessTracker = ({ activeSection = 'services' }) => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('All');
  const [groups, setGroups] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [logs, setLogs] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startingServices, setStartingServices] = useState({});
  const [stoppingServices, setStoppingServices] = useState({});
  const [restartingServices, setRestartingServices] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreatingService, setIsCreatingService] = useState(false);
  // Fetch services and groups on component mount
  useEffect(() => {
    fetchServices();
    fetchGroups();
  }, []);

  // Fetch all services or filtered by group
  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await processTrackerService.fetchServices(selectedGroup === 'All' ? 'all' : selectedGroup, searchTerm);
      setServices(response);
      setError(null);
      return response;
    } catch (err) {
      setError(`Failed to fetch services: ${err.message}`);
      console.error('Error fetching services:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh with progress animation - only fetches status, doesn't restart services
  const handleManualRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setRefreshProgress(0);

    // Create a progress animation
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += 5;
      setRefreshProgress(Math.min(progress, 90)); // Only go up to 90% until actual completion
    }, 50);

    try {
      await fetchServices();

      // Complete the progress bar
      clearInterval(progressInterval);
      setRefreshProgress(100);

      // Reset progress after a short delay
      setTimeout(() => {
        setRefreshProgress(0);
        setIsRefreshing(false);
      }, 500);
    } catch (error) {
      clearInterval(progressInterval);
      setRefreshProgress(0);
      setIsRefreshing(false);
    }
  };

  // Fetch all available service groups
  const fetchGroups = async () => {
    try {
      const response = await processTrackerService.fetchGroups();
      setGroups(['All', ...response]);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  // Fetch logs for a specific service
  const fetchLogs = async (serviceId) => {
    try {
      const response = await processTrackerService.fetchLogs(serviceId);
      setLogs(response.logs);
      setSelectedService(services.find(s => s.id === serviceId));
      setShowLogs(true);
    } catch (err) {
      setError(`Failed to fetch logs: ${err.message}`);
      console.error('Error fetching logs:', err);
    }
  };

  // Start a service with progress tracking
  const startService = async (serviceId) => {
    try {
      setActionInProgress(serviceId);

      // Add to starting services with initial progress
      setStartingServices(prev => ({
        ...prev,
        [serviceId]: { progress: 0, status: 'starting' }
      }));

      // Start progress animation
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 5;
        setStartingServices(prev => ({
          ...prev,
          [serviceId]: { ...prev[serviceId], progress: Math.min(progress, 90) }
        }));
      }, 200);

      const response = await processTrackerService.startService(serviceId);

      // Clear interval when response is received
      clearInterval(progressInterval);

      if (response.success !== true) {
        throw new Error('Service did not start successfully');
      }

      // Set to 100% when successful
      setStartingServices(prev => ({
        ...prev,
        [serviceId]: { progress: 100, status: 'success' }
      }));

      // Remove from starting services after a delay
      setTimeout(() => {
        setStartingServices(prev => {
          const newState = { ...prev };
          delete newState[serviceId];
          return newState;
        });
      }, 1000);

      await fetchServices();
    } catch (err) {
      // Handle error state in progress tracking
      setStartingServices(prev => ({
        ...prev,
        [serviceId]: { progress: 100, status: 'error' }
      }));

      setTimeout(() => {
        setStartingServices(prev => {
          const newState = { ...prev };
          delete newState[serviceId];
          return newState;
        });
      }, 1000);

      setError(`Failed to start service: ${err.message}`);
      console.error('Error starting service:', err);
    } finally {
      setActionInProgress(null);
    }
  };

  // Stop a service with progress tracking
  const stopService = async (serviceId) => {
    try {
      setActionInProgress(serviceId);

      // Add to stopping services with initial progress
      setStoppingServices(prev => ({
        ...prev,
        [serviceId]: { progress: 0, status: 'stopping' }
      }));

      // Start progress animation
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 5;
        setStoppingServices(prev => ({
          ...prev,
          [serviceId]: { ...prev[serviceId], progress: Math.min(progress, 90) }
        }));
      }, 200);

      try {
        const response = await processTrackerService.stopService(serviceId);

        // Clear interval when response is received
        clearInterval(progressInterval);

        // Check the success field from the ServiceAction response
        if (!response.success) {
          throw new Error(response.result || 'Service did not stop successfully');
        }

        // Set to 100% when successful
        setStoppingServices(prev => ({
          ...prev,
          [serviceId]: { progress: 100, status: 'success' }
        }));

        // Remove from stopping services after a delay
        setTimeout(() => {
          setStoppingServices(prev => {
            const newState = { ...prev };
            delete newState[serviceId];
            return newState;
          });
        }, 1000);

        // Refresh the services list to show updated status
        await fetchServices();
      } catch (err) {
        clearInterval(progressInterval);

        // Check if this is the specific validation error for null stop_command
        const errorMessage = err.message || '';
        const isNullStopCommandError =
          errorMessage.includes('stop_command') &&
          (errorMessage.includes('Input should be a valid string') ||
            errorMessage.includes('input_value=None') ||
            errorMessage.includes('NoneType'));

        // Handle error state in progress tracking
        setStoppingServices(prev => ({
          ...prev,
          [serviceId]: {
            progress: 100,
            status: 'error',
            errorDetails: isNullStopCommandError
              ? 'This service has no stop command defined in its configuration'
              : errorMessage
          }
        }));

        setTimeout(() => {
          setStoppingServices(prev => {
            const newState = { ...prev };
            delete newState[serviceId];
            return newState;
          });
        }, 3000);

        // Set a user-friendly error message
        const serviceName = services.find(s => s.id === serviceId)?.name || serviceId;
        const friendlyErrorMessage = isNullStopCommandError
          ? `Cannot stop "${serviceName}": No stop command is configured for this service.`
          : `Failed to stop service: ${errorMessage}`;

        setError(friendlyErrorMessage);
        console.error('Error stopping service:', err);

        // Try to refresh services anyway to get the latest status
        try {
          await fetchServices();
        } catch (fetchErr) {
          console.error('Error refreshing services after stop attempt:', fetchErr);
        }
      }
    } finally {
      setActionInProgress(null);
    }
  };

  const handleCreateService = async (serviceData) => {
    try {
      setIsCreatingService(true);
      const response = await processTrackerService.createService(serviceData);

      // Close the modal and refresh the services list
      setShowCreateModal(false);
      await fetchServices();

      // Show success message
      setError(null);
      // You could add a success toast/notification here

      return response;
    } catch (err) {
      setError(`Failed to create service: ${err.message}`);
      console.error('Error creating service:', err);
    } finally {
      setIsCreatingService(false);
    }
  };

  const restartService = async (serviceId) => {
    try {
      setActionInProgress(serviceId);

      setRestartingServices(prev => ({
        ...prev,
        [serviceId]: { progress: 0, status: 'restarting' }
      }));

      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 5;
        setRestartingServices(prev => ({
          ...prev,
          [serviceId]: { ...prev[serviceId], progress: Math.min(progress, 90) }
        }));
      }, 200);

      const response = await processTrackerService.restartService(serviceId);

      clearInterval(progressInterval);

      if (response.success !== true) {
        throw new Error('Service did not restart successfully');
      }

      // Set to 100% when successful
      setRestartingServices(prev => ({
        ...prev,
        [serviceId]: { progress: 100, status: 'success' }
      }));

      // Remove from restarting services after a delay
      setTimeout(() => {
        setRestartingServices(prev => {
          const newState = { ...prev };
          delete newState[serviceId];
          return newState;
        });
      }, 1000);

      await fetchServices();
    } catch (err) {
      // Handle error state in progress tracking
      setRestartingServices(prev => ({
        ...prev,
        [serviceId]: { progress: 100, status: 'error' }
      }));

      setTimeout(() => {
        setRestartingServices(prev => {
          const newState = { ...prev };
          delete newState[serviceId];
          return newState;
        });
      }, 1000);

      setError(`Failed to restart service: ${err.message}`);
      console.error('Error restarting service:', err);
    } finally {
      setActionInProgress(null);
    }
  };

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status color class
  const getStatusColorClass = (status) => {
    switch (status) {
      case 'running':
        return 'bg-green-100/70 text-green-800 border border-green-200';
      case 'stopped':
        return 'bg-red-100/70 text-red-800 border border-red-200';
      case 'error':
        return 'bg-yellow-100/70 text-yellow-800 border border-yellow-200';
      default:
        return 'bg-gray-100/70 text-gray-800 border border-gray-200';
    }
  };

  // Unified service operation progress component
  const ServiceOperationProgress = () => {
    // Get all operations in progress
    const allOperations = [
      ...Object.keys(startingServices).map(id => ({ id, type: 'start', info: startingServices[id] })),
      ...Object.keys(stoppingServices).map(id => ({ id, type: 'stop', info: stoppingServices[id] })),
      ...Object.keys(restartingServices).map(id => ({ id, type: 'restart', info: restartingServices[id] }))
    ];

    if (allOperations.length === 0) return null;

    // Sort operations to show the most recent at the bottom
    const sortedOperations = allOperations.sort((a, b) => {
      // Prioritize in-progress operations
      if (a.info.status === 'starting' || a.info.status === 'stopping' || a.info.status === 'restarting') return -1;
      if (b.info.status === 'starting' || b.info.status === 'stopping' || b.info.status === 'restarting') return 1;
      return 0;
    });

    return (
      <div className="fixed bottom-4 right-4 flex flex-col space-y-2 z-50">
        {sortedOperations.map((op, index) => {
          const { id, type, info } = op;

          const getProgressColorClass = () => {
            switch (info.status) {
              case 'success':
                return 'bg-green-500';
              case 'error':
                return 'bg-red-500';
              case 'starting':
                return 'bg-blue-500';
              case 'stopping':
                return 'bg-red-500';
              case 'restarting':
                return 'bg-yellow-500';
              default:
                return 'bg-gray-500';
            }
          };

          const getStatusText = () => {
            switch (type) {
              case 'start':
                return info.status === 'starting' ? 'Starting service...' :
                  info.status === 'success' ? 'Service started successfully' :
                    'Failed to start service';
              case 'stop':
                return info.status === 'stopping' ? 'Stopping service...' :
                  info.status === 'success' ? 'Service stopped successfully' :
                    'Failed to stop service';
              case 'restart':
                return info.status === 'restarting' ? 'Restarting service...' :
                  info.status === 'success' ? 'Service restarted successfully' :
                    'Failed to restart service';
              default:
                return 'Processing...';
            }
          };

          const handleDismiss = () => {
            if (type === 'start') {
              setStartingServices(prev => {
                const newState = { ...prev };
                delete newState[id];
                return newState;
              });
            } else if (type === 'stop') {
              setStoppingServices(prev => {
                const newState = { ...prev };
                delete newState[id];
                return newState;
              });
            } else if (type === 'restart') {
              setRestartingServices(prev => {
                const newState = { ...prev };
                delete newState[id];
                return newState;
              });
            }
          };

          const isInProgress = info.status === 'starting' || info.status === 'stopping' || info.status === 'restarting';

          return (
            <div
              key={`${type}-${id}`}
              className="bg-white/90 backdrop-blur-md rounded-lg shadow-lg p-4 border border-gray-200/50 w-80"
              style={{ marginBottom: index === sortedOperations.length - 1 ? 0 : '0.5rem' }}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-gray-800">
                  {getStatusText()}
                </span>
                {!isInProgress && (
                  <button
                    onClick={handleDismiss}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    &times;
                  </button>
                )}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all duration-300 ${getProgressColorClass()}`}
                  style={{ width: `${info.progress}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {services.find(s => s.id === id)?.name || id}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  const RefreshButton = () => (
    <button
      onClick={handleManualRefresh}
      disabled={isRefreshing}
      className="flex items-center text-primary text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-primaryLight disabled:text-primryLight disabled:cursor-not-allowed"
    >
      {isRefreshing ? (
        <Loader size={16} className="mr-1 animate-spin" />
      ) : (
        <RefreshCw size={16} className="mr-1" />
      )}
      {isRefreshing ? 'Refreshing...' : 'Refresh'}
    </button>
  );

  const ActionButton = ({ onClick, disabled, icon, activeIcon, isActive, className, title }) => {
    const isLoading = actionInProgress === title.split(' ')[0].toLowerCase();

    return (
      <button
        onClick={onClick}
        disabled={disabled || actionInProgress !== null}
        className={`p-2 rounded-full shadow-sm transition-all duration-200 ${className} ${isLoading ? 'bg-primaryLight text-primaryDark cursor-wait' : ''
          }`}
        title={title}
      >
        {isLoading ? (
          <Loader size={14} className="animate-spin" />
        ) : isActive ? (
          activeIcon
        ) : (
          icon
        )}
      </button>
    );
  };

  return (
    // Glassy background with subtle gradient
    <div className="flex flex-col h-screen p-4 sm:p-6">
      
        

    

      {/* DDLC Pipeline section */}
      {activeSection === 'ddlc' && (
        <div className="flex-1 overflow-auto flex flex-col min-h-0">
          <DdlcPipelineLogs />
        </div>
      )}

      {/* Main content area - will take up remaining height */}
      {activeSection === 'services' && <><div className="flex-1 overflow-y-scroll [scrollbar-width:none] ">
        {/* Error message with glassy effect */}
        {error && (
          <div className="bg-red-50/90 backdrop-blur-sm border-l-4 border-red-500 text-red-700 p-4 mb-6 flex items-center rounded-lg shadow-md">
            <AlertTriangle className="mr-2" size={20} />
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-700 hover:text-red-900 transition-colors"
            >
              &times;
            </button>
          </div>
        )}


        {/* Service count and refresh button */}
        <div className="flex items-center justify-between px-4 py-2">
          <div className="text-sm text-gray-500">
            Showing <span className="font-medium text-primaryDark">{filteredServices.length}</span> of <span className="font-medium text-primaryDark">{services.length}</span> services
          </div>
          <RefreshButton />
        </div>

        {/* Manual refresh progress bar */}
        {isRefreshing && (
          <div className="px-4 mb-1">
            <div className="w-full bg-gray-200 rounded-full h-1">
              <div
                className="h-1 rounded-full bg-primary transition-all duration-300"
                style={{ width: `${refreshProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Services Table with glassy effect */}
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3 pl-6 pr-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-200">
                  Service Name
                </th>
                <th className="py-3 px-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-200">
                  Group
                </th>
                <th className="py-3 px-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-200">
                  Status
                </th>
                <th className="py-3 px-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-200">
                  Port
                </th>
                <th className="py-3 px-3 text-left text-xs font-medium text-primary uppercase tracking-wider border-b border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white backdrop-blur-sm divide-y divide-gray-200/70">
              {loading && services.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                      <span className="ml-2">Loading services...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredServices.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No services found
                  </td>
                </tr>
              ) : (
                filteredServices.map(service => (
                  <tr key={service.id} className="hover:bg-blue-50/50 transition-colors">
                    <td className="py-3 pl-6 pr-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{service.name}</div>
                      <div className="text-sm text-gray-500">{service.id}</div>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100/70 text-primaryDark border border-blue-200 backdrop-blur-sm">
                        {service.group_name}
                      </span>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full backdrop-blur-sm ${getStatusColorClass(service.status)}`}>
                        {service.status}
                      </span>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap text-sm text-gray-500">
                      {service.port || 'N/A'}
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <ActionButton
                          onClick={() => startService(service.id)}
                          disabled={service.status === 'running' || actionInProgress !== null}
                          icon={<Play size={14} />}
                          className={
                            service.status === 'running'
                              ? 'bg-gray-100/70 text-gray-400 cursor-not-allowed'
                              : 'bg-green-100/70 text-green-600 hover:bg-green-200/90 hover:shadow-md'
                          }
                          title="Start Service"
                        />
                        <ActionButton
                          onClick={() => stopService(service.id)}
                          disabled={service.status !== 'running' || actionInProgress !== null}
                          icon={<Square size={14} />}
                          className={
                            service.status !== 'running'
                              ? 'bg-gray-100/70 text-gray-400 cursor-not-allowed'
                              : 'bg-red-100/70 text-red-600 hover:bg-red-200/90 hover:shadow-md'
                          }
                          title="Stop Service"
                        />
                        <ActionButton
                          onClick={() => restartService(service.id)}
                          disabled={actionInProgress !== null || service.status !== 'running'}
                          icon={<RotateCw size={14} />}
                          className={
                            service.status !== 'running'
                              ? 'bg-gray-100/70 text-gray-400 cursor-not-allowed'
                              : 'bg-yellow-100/70 text-yellow-600 hover:bg-yellow-200/90 hover:shadow-md shadow-sm backdrop-blur-sm'
                          }
                          title="Restart Service"
                        />
                        {service.log_path && (
                          <ActionButton
                            onClick={() => fetchLogs(service.id)}
                            disabled={actionInProgress !== null}
                            icon={<FileText size={14} />}
                            className="bg-blue-100/70 text-primaryDark hover:bg-blue-200/90 hover:shadow-md shadow-sm backdrop-blur-sm"
                            title="View Logs"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Logs Modal with glassy effect */}
      {showLogs && selectedService && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col border border-gray-100/50">
            <div className="px-6 py-4 border-b border-gray-200/50 flex justify-between items-center bg-gray-50/80">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <FileText className="mr-2 text-primary" size={18} />
                Logs for {selectedService.name}
              </h3>
              <button
                onClick={() => setShowLogs(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none hover:bg-gray-100/70 p-1 rounded-full transition-colors"
              >
                &times;
              </button>
            </div>
            <div className="p-6 flex-grow overflow-auto">
              <pre className="bg-gray-900/95 text-gray-100 p-4 rounded-lg overflow-auto h-96 text-sm font-mono shadow-inner border border-gray-700/50">
                {logs || 'No logs available'}
              </pre>
            </div>
            <div className="px-6 py-4 border-t border-gray-200/50 flex justify-end bg-gray-50/80">
              <button
                onClick={() => fetchLogs(selectedService.id)}
                className="mr-2 px-4 py-2 bg-blue-500/90 text-white rounded-lg hover:bg-blue-600 flex items-center backdrop-blur-sm shadow-md transition-all duration-300 hover:shadow-blue-200/50"
                disabled={actionInProgress !== null}
              >
                {actionInProgress === 'logs' ? (
                  <Loader size={14} className="mr-2 animate-spin" />
                ) : (
                  <RotateCw size={14} className="mr-2" />
                )}
                Refresh Logs
              </button>
              <button
                onClick={() => setShowLogs(false)}
                className="px-4 py-2 bg-gray-200/80 text-gray-800 rounded-lg hover:bg-gray-300/90 backdrop-blur-sm shadow-md transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service operation progress notifications */}
      <ServiceOperationProgress />
      </>}
    </div>
  );
};

export default ProcessTracker;